import api from '@/core/api/client';

/** ----------------------------- Domain types ----------------------------- */

export interface MigrationLegacyPhone {
  /** Meta phoneNumberId */
  id: string;
  label: string;
  number: string;
  hasHistory?: boolean;
  lastMessageAt?: string;
  /** false = no ACTIVE Cloud channel on v2 */
  linkedToV2?: boolean;
  v2CompanyId?: string;
}

export interface MigrationLegacyClient {
  id: string;
  name: string;
  legacyId: string;
  segment: string;
  ownerEmail: string;
  updatedAt: string;
  phones: MigrationLegacyPhone[];
}

export interface MigrationLegacyClientsListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface MigrationLegacyClientsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface MigrationLegacyClientsListResponse {
  data: MigrationLegacyClient[];
  pagination: MigrationLegacyClientsPagination;
}

export type MigrationPhoneSelection = {
  partnerId: string;
  phoneNumberId: string;
};

export type MigrationStartPayload = {
  phones: MigrationPhoneSelection[];
  /** History window in days (1–180). Backend default: 7. */
  days?: number;
};

export type MigrationStartedJob = {
  jobId: string;
  phoneNumberId: string;
  companyId: string;
  channelId: string;
  days: number;
};

export type MigrationStartResult = {
  jobs: MigrationStartedJob[];
  days: number;
};

export type MigrationJobStatus =
  | 'pending'
  | 'fetching'
  | 'syncing'
  | 'completed'
  | 'failed';

export type MigrationJobListItem = {
  jobId: string;
  channelId: string;
  companyId: string;
  status: MigrationJobStatus;
  totalChats: number;
  processedChats: number;
  totalConversationsCreated: number;
  totalMessagesInserted: number;
  totalMessagesSkipped: number;
  errors: number;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  companyName: string;
  channelName: string;
  displayPhone: string;
  progressPercent: number | null;
  triggeredByStaffName: string | null;
  triggeredByStaffEmail: string | null;
};

export type MigrationJobsListParams = {
  limit?: number;
  status?: MigrationJobStatus;
};

/** ------------------------------ Service --------------------------------- */

export const migrationService = {
  /**
   * Listagem leve (nome + id legado), paginada. Sem telefones.
   */
  async listLegacyClients(
    params: MigrationLegacyClientsListParams = {}
  ): Promise<MigrationLegacyClientsListResponse> {
    const { data } = await api.get<{
      success: boolean;
      data: MigrationLegacyClient[];
      pagination: MigrationLegacyClientsPagination;
    }>('/migration/legacy-clients', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        search: params.search || undefined,
      },
    });
    return { data: data.data, pagination: data.pagination };
  },

  /**
   * Detalhe de um parceiro com telefones WABA + match v2.
   */
  async getLegacyClient(partnerId: string): Promise<MigrationLegacyClient> {
    const { data } = await api.get<{ success: boolean; data: MigrationLegacyClient }>(
      `/migration/legacy-clients/${encodeURIComponent(partnerId)}`
    );
    return data.data;
  },

  /**
   * Queues migration jobs for the given partnerId + Meta phoneNumberIds.
   * Returns 202 with the enqueued jobs.
   */
  async runMigration(payload: MigrationStartPayload): Promise<MigrationStartResult> {
    const { data } = await api.post<{ success: boolean; data: MigrationStartResult }>(
      '/migration/migrate',
      { phones: payload.phones, days: payload.days }
    );
    return data.data;
  },

  /** Staff: recent message-sync jobs (cross-company), newest first. */
  async listJobs(params: MigrationJobsListParams = {}): Promise<MigrationJobListItem[]> {
    const { data } = await api.get<{ success: boolean; data: MigrationJobListItem[] }>(
      '/migration/jobs',
      {
        params: {
          limit: params.limit ?? 50,
          status: params.status || undefined,
        },
      }
    );
    return data.data;
  },
};
